import Skeleton from '../../models/skeleton'
import ErrorModel from '../../models/error'
import analyticsEvents from './events'
import { inIframe } from '../../util/in_iframe'
import appInfo from '@polleverywhere/slideware-bindings/lib/app_info'

/**
 * Returns a Backbone model that tracks the current screen and Events
 *
 * @event action
 *   @property {String} Name of action in the current view
 *   @property {Object} Additional action related information
 *
 * @event event
 *   @property {String} Name of application event
 *   @property {Object} Additional event related information
 *
 * @property {String} mode Application mode (currently "standalone" | "integration")
 * @property {String} screen Current view state (i.e. "activity_group/view_activity/edit")
 * @property {?Number} userId Current user ID
 */

export default (app) => {
  // if not a slideware platform (presenter/liveslides/electron/google), then delegate to capacitor platform (web/ios/android)
  let platform = appInfo.platform
  if (platform.length === 0) {
    platform = window.Capacitor?.platform
  }

  // Use model to track changes. Setting multiple times
  // won't trigger the api. Only changes will.
  const tracker = new Backbone.Model({
    role: undefined,
    mode: undefined,
    screen: undefined,
    userId: undefined,
    activePresenter: undefined,
    iframed: inIframe() || undefined,
    isNative: window.Capacitor?.isNative,
    platform: platform,
    os: appInfo.operatingSystem
  })

  // Start tracking the state once it's ready
  const dispatcher = _.clone(Backbone.Events)
  dispatcher.listenTo(app, 'reset:state', () =>
    trackApp(dispatcher, app, tracker)
  )

  return tracker
}

// Current view state
function screen (state) {
  const page = state.get('page')
  const appState = state.appStates?.[page]

  if (!appState) { return page }

  return [
    page,
    appState.get('page'),
    appState.get('activity') ? 'activity' : null,
    appState.get('pageAction')
  ].filter(node => !!node)
    .join('/')
}

// Track all the things
function trackApp (dispatcher, app, tracker) {
  const updateTrackerState = () =>
    tracker.set({
      mode: app.state.get('mode'),
      role: app.options.role,
      screen: screen(app.state),
      activePresenter: app.state.activePresenter?.id
    })

  updateTrackerState()

  const activitiesState = app.state.appStates && app.state.appStates.activities
  const slidewareState = app.state.appStates && app.state.appStates.slideware
  dispatcher.listenTo(app.state, 'session:start', () =>
    tracker.set({ userId: app.state.get('user').id })
  )

  // Update tracker state when application state changes
  dispatcher.listenTo(app.state, 'change', updateTrackerState)
  dispatcher.listenTo(activitiesState, 'change', updateTrackerState)
  dispatcher.listenTo(slidewareState, 'change', updateTrackerState)

  // Trigger an event when activity reaches 4 results
  // (Ideally this would be in analytics_events as well)
  dispatcher.listenTo(activitiesState, 'change:activity', (state, activity, opts = {}) => {
    if (activity instanceof Skeleton) { return }
    if (activity instanceof ErrorModel) { return }

    const previousActivity = state.previous('activity')
    if (previousActivity) {
      dispatcher.stopListening(previousActivity)
    }

    if (!activity) { return }

    // Listen to results count changes and trigger an event when it reaches 4 results
    dispatcher.listenTo(activity, 'change:results_count', (_, count) => {
      if (
        (count >= 4) &&
        (state.get('page') === 'presenter')
      ) {
        tracker.trigger('action', 'activity:has_4_results')

        // Stop listening to the activity state, as we only need this one event
        dispatcher.stopListening(activity)
      }
    })
  })

  // Track all the events defined in ./events.js
  // See file for full list and interface documentation
  analyticsEvents.forEach(({ name, appState, event, filter = () => true, properties }) => {
    const model = appState && app.state.appStates?.[appState]
      ? app.state.appStates[appState]
      : app

    dispatcher.listenTo(model, event, (state, value, opts = {}) => {
      if (!filter(state, value, opts)) { return }
      tracker.trigger('action',
        name(state, value, opts),
        properties && properties(state, value, opts)
      )
    })
  })
}
